// Formatted messages: a small Markdown subset, typed with the FormatBar under a message box and shown with
// MessageBody. The portal shows firms the same subset (Upstream Portal FeedbackMarkdown); the bell gets plain text
// (@upm/domain feedbackPlainText). Anything outside the subset shows as its text, and raw HTML never renders.
// A teammate tag, [@Name](staff:<id>) (admin/Mentions.tsx), shows highlighted; it is never a link.
import type { KeyboardEvent, RefObject } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Bold, Italic, Link2, List, ListOrdered } from 'lucide-react';
import { cx } from '../../ui';

const ALLOWED = ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a'];
const SAFE_URL = /^(https?:|mailto:|staff:)/i;

const COMPONENTS: Components = {
  p: ({ children }) => <p className="m-0">{children}</p>,
  ul: ({ children }) => <ul className="m-0 list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="m-0 list-decimal pl-5">{children}</ol>,
  a: ({ href, children }) => {
    if (href?.startsWith('staff:')) return <span className="rounded bg-brand-soft px-1 font-semibold text-brand">{children}</span>;
    return href
      ? <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline">{children}</a>
      : <>{children}</>;
  },
};

/** A message body: bold, italic, lists and http(s)/mailto links. A single newline stays a line break. */
export function MessageBody({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-2 break-words', className)}>
      <Markdown remarkPlugins={[remarkBreaks]} allowedElements={ALLOWED} unwrapDisallowed skipHtml components={COMPONENTS}
        urlTransform={(url) => (SAFE_URL.test(url) ? url : null)}>
        {text}
      </Markdown>
    </div>
  );
}

export type Format = 'bold' | 'italic' | 'bullets' | 'numbers' | 'link';
type Edit = { value: string; start: number; end: number };

const WRAP: Partial<Record<Format, string>> = { bold: '**', italic: '*' };

/** Applies a format to the selection `start`–`end` of `value`. Returns the new text and what to select next. */
export function applyFormat(format: Format, value: string, start: number, end: number): Edit {
  const chosen = value.slice(start, end);
  const mark = WRAP[format];
  if (mark) {
    // Already wrapped: take the marks off again.
    if (value.slice(start - mark.length, start) === mark && value.slice(end, end + mark.length) === mark) {
      return { value: value.slice(0, start - mark.length) + chosen + value.slice(end + mark.length), start: start - mark.length, end: end - mark.length };
    }
    return { value: value.slice(0, start) + mark + chosen + mark + value.slice(end), start: start + mark.length, end: end + mark.length };
  }
  if (format === 'link') {
    const words = chosen || 'link text';
    const url = 'https://';
    const out = `[${words}](${url})`;
    // Select the address, ready to type or paste over.
    const at = start + words.length + 3;
    return { value: value.slice(0, start) + out + value.slice(end), start: chosen ? at : start + 1, end: chosen ? at + url.length : start + 1 + words.length };
  }
  // A list: every line the selection touches.
  const from = value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end);
  const to = lineEnd === -1 ? value.length : lineEnd;
  const lines = value.slice(from, to).split('\n');
  const marker = format === 'bullets' ? /^- / : /^\d+\. /;
  const on = lines.every((l) => marker.test(l));
  const next = lines.map((l, i) => {
    const bare = l.replace(/^(- |\d+\. )/, '');
    if (on) return bare;
    return format === 'bullets' ? `- ${bare}` : `${i + 1}. ${bare}`;
  }).join('\n');
  return { value: value.slice(0, from) + next + value.slice(to), start: from, end: from + next.length };
}

const BUTTONS: Array<{ format: Format; label: string; icon: typeof Bold }> = [
  { format: 'bold', label: 'Bold (⌘B)', icon: Bold },
  { format: 'italic', label: 'Italic (⌘I)', icon: Italic },
  { format: 'bullets', label: 'Bulleted list', icon: List },
  { format: 'numbers', label: 'Numbered list', icon: ListOrdered },
  { format: 'link', label: 'Link (⌘K)', icon: Link2 },
];
const KEYS: Record<string, Format> = { b: 'bold', i: 'italic', k: 'link' };

/**
 * Formatting for a message box. Put `bar` under the TextArea, and pass `onKeyDown` to it for ⌘B / ⌘I / ⌘K
 * (Ctrl on Windows).
 */
export function useFormatting(box: RefObject<HTMLTextAreaElement | null>, setText: (text: string) => void) {
  const apply = (format: Format) => {
    const el = box.current;
    if (!el) return;
    const edit = applyFormat(format, el.value, el.selectionStart, el.selectionEnd);
    setText(edit.value);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(edit.start, edit.end); });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const format = (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey ? KEYS[e.key.toLowerCase()] : undefined;
    if (!format) return;
    e.preventDefault();
    apply(format);
  };
  const bar = (
    <div role="toolbar" aria-label="Formatting" className="-mt-1.5 flex flex-wrap items-center gap-0.5">
      {BUTTONS.map(({ format, label, icon: Icon }) => (
        <button key={format} type="button" aria-label={label.replace(/ \(.*\)$/, '')} title={label}
          onMouseDown={(e) => e.preventDefault()} onClick={() => apply(format)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-ink-2 hover:bg-subtle hover:text-ink">
          <Icon aria-hidden size={15} />
        </button>
      ))}
    </div>
  );
  return { bar, onKeyDown };
}
